import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  Renderer2,
} from '@angular/core';
import {
  TranslationService,
  Language,
} from '../../appServices/translation.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-global-language-switcher',
  templateUrl: './global-language-switcher.component.html',
  styleUrls: ['./global-language-switcher.component.scss'],
  standalone: false,
})
export class GlobalLanguageSwitcherComponent implements OnInit, OnDestroy {
  supportedLanguages: Language[] = [
    // Fallback languages in case service fails
    { code: 'en', name: 'English', flag: 'assets/images/flags/en.jpg' },
    { code: 'fr', name: 'Français', flag: 'assets/images/flags/fr.jpg' },
    { code: 'de', name: 'Deutsch', flag: 'assets/images/flags/de.jpg' },
    { code: 'it', name: 'Italiano', flag: 'assets/images/flags/it.jpg' },
  ];
  currentLanguage: string = 'en';
  isDarkMode: boolean = false;
  isLoading: boolean = false;
  loadingLanguage: string = '';

  private subscription: Subscription = new Subscription();
  private themeObserver?: MutationObserver;

  constructor(
    private translationService: TranslationService,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {
    // Initialize immediately to prevent empty array issues
    this.supportedLanguages = this.translationService.getSupportedLanguages();
    this.currentLanguage = this.translationService.getCurrentLanguage();
  }

  ngOnInit(): void {
    this.supportedLanguages = this.translationService.getSupportedLanguages();
    this.currentLanguage = this.translationService.getCurrentLanguage();

    this.detectTheme();
    this.setupThemeObserver();

    // Subscribe to language changes
    this.subscription.add(
      this.translationService.currentLanguage$.subscribe((lang) => {
        this.currentLanguage = lang;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  get currentLanguageInfo(): Language {
    return (
      this.supportedLanguages.find(
        (lang) => lang.code === this.currentLanguage
      ) || this.supportedLanguages[0]
    );
  }

  get themeClasses(): string {
    return this.isDarkMode ? 'dark-theme' : 'light-theme';
  }

  async selectLanguage(languageCode: string): Promise<void> {
    if (this.isLoading || languageCode === this.currentLanguage) return;

    this.isLoading = true;
    this.loadingLanguage = languageCode;

    try {
      await this.translationService.setLanguage(languageCode);
      console.log(`🌐 Global Language Switcher: Changed to ${languageCode}`);
    } catch (error) {
      console.error(
        '🌐 Global Language Switcher: Error changing language:',
        error
      );
    } finally {
      this.isLoading = false;
      this.loadingLanguage = '';
    }
  }

  private detectTheme(): void {
    const body = document.body;
    const theme = body.getAttribute('data-theme');

    if (theme) {
      this.isDarkMode = theme === 'dark';
    } else {
      // Fallback to system preference
      this.isDarkMode = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches;
    }
  }

  private setupThemeObserver(): void {
    const body = document.body;

    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-theme'
        ) {
          this.detectTheme();
        }
      });
    });

    this.themeObserver.observe(body, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
  }

  trackByLanguage(index: number, language: Language): string {
    return language.code;
  }
}
